// ============================================================================
// Portrait & tendances (F-764..770, F-779) — Zone B
// ============================================================================
// Check-ins (tendances + grille trimestrielle + dialogue), pouls individuel (12
// mois — M-774 : le DTO serveur applique deja l'exclusion manager), augmentations
// (M-762), forces Gallup/Nova (+ edition, M-168), objectifs (M-765/770).
// Aucune garde d'autorite locale ; le hub ne rend Zone B que derriere le grant.

import { useState } from 'react';
import { GS_COMPONENTS } from '@gs/ui';
import { Plus } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { Card, CardHeader, Badge, StatCard, DataRow, EmptyRow, Modal, ModalFooter, Sparkline, MiniBars, GaugeBar, type BadgeTone } from './ui';
import { CHECKIN_QUESTIONS, CHECKIN_MAX, checkinAverage } from '../../lib/checkin/questions';
import { MEMBER_PULSE_MAX } from '../../lib/member-pulse/questions';
import { GALLUP_DOMAIN_LABELS, GALLUP_STRENGTHS_BY_DOMAIN, isValidStrength, type GallupDomain } from '../../lib/profile/gallup';
import { NOVA_COLORS, NOVA_LABELS, NOVA_DESCRIPTIONS, NOVA_BAR_CLASSES } from '../../lib/profile/nova';
import type { RaiseRow } from '../../lib/salary/raise-history';
import {
  managementErrorCode, useSubmitCheckin, useSetStrengths, useCreateObjective, useUpdateObjectiveStatus, useDeleteObjective,
  type EmployeeCheckin, type MemberPulse, type EmployeeObjective, type EmployeeStrengths,
} from '../../hooks/useManagement';

type OnError = (code: string) => void;
const label = 'text-meta font-medium text-slate-600 dark:text-slate-300';
const CAD0 = new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 });

// ─── F-764 Tendances des check-ins ────────────────────────────────────────────

export function CheckinTrends({ checkins }: { checkins: EmployeeCheckin[] }) {
  const { t } = useTranslation();
  const ordered = [...checkins].sort((a, b) => a.year - b.year || a.quarter - b.quarter);
  const series = CHECKIN_QUESTIONS.map((q) => {
    const values = ordered.map((c) => c.answers.find((a) => a.key === q.key)?.rating).filter((r): r is number => r !== undefined && r > 0);
    const latest = values.at(-1) ?? null;
    const prev = values.at(-2) ?? null;
    return { key: q.key, label: q.label, values, latest, delta: latest !== null && prev !== null ? latest - prev : null };
  });
  const hasData = series.some((s) => s.values.length > 0);
  const deltaCls = (d: number | null) => (d === null || d === 0 ? 'text-slate-500 dark:text-slate-400' : d > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-brand-600 dark:text-brand-400');

  return (
    <Card>
      <CardHeader title={t('gest:management.checkin.trendsTitle')} />
      {!hasData ? <EmptyRow title={t('gest:management.checkin.trendsEmpty')} description={t('gest:management.checkin.trendsEmptyHint')} /> : (
        <ul className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
          {series.map((s) => (
            <li key={s.key} className="flex items-center justify-between gap-3 border-b border-slate-200 pb-2.5 last:border-b-0 dark:border-slate-700">
              <div className="min-w-0">
                <p className="truncate text-field font-medium text-slate-700 dark:text-slate-200">{s.label}</p>
                <p className="text-chip tabular-nums text-slate-500 dark:text-slate-400">
                  {s.latest !== null ? <>{s.latest}/{CHECKIN_MAX}{s.delta !== null && s.delta !== 0 ? <span className={`ml-1.5 ${deltaCls(s.delta)}`}>{s.delta > 0 ? `+${s.delta}` : s.delta}</span> : null}</> : t('gest:management.checkin.noData')}
                </p>
              </div>
              {s.values.length >= 2 ? <Sparkline data={s.values} className={deltaCls(s.delta)} /> : <span className="text-chip text-slate-500 dark:text-slate-400">{s.values.length === 1 ? t('gest:management.checkin.onePoint') : '—'}</span>}
            </li>
          ))}
        </ul>
      )}
      <p className="mt-2 text-chip text-slate-500 dark:text-slate-400">{t('gest:management.checkin.trendsFooter')}</p>
    </Card>
  );
}

// ─── F-765 Check-in trimestriel (grille + dialogue) ──────────────────────────

export function CheckinCard({ email, year, currentQuarter, checkins, onError }: {
  email: string; year: number; currentQuarter: number; checkins: EmployeeCheckin[]; onError: OnError;
}) {
  const { t } = useTranslation();
  const submit = useSubmitCheckin();
  const [open, setOpen] = useState(false);
  const [quarter, setQuarter] = useState(currentQuarter);
  const [note, setNote] = useState('');
  const [ratings, setRatings] = useState<Record<string, string>>(() => Object.fromEntries(CHECKIN_QUESTIONS.map((q) => [q.key, '3'])));
  const byQuarter = new Map(checkins.filter((c) => c.year === year).map((c) => [c.quarter, c]));
  const tone = (a: number): BadgeTone => (a >= 4 ? 'success' : a >= 3 ? 'info' : 'warning');

  function send() {
    const r: Record<string, number> = {};
    for (const q of CHECKIN_QUESTIONS) r[q.key] = Number(ratings[q.key] ?? 0);
    submit.mutate({ email, payload: { year, quarter, note: note.trim() || null, ratings: r } }, {
      onSuccess: () => setOpen(false),
      onError: (x) => onError(managementErrorCode(x)),
    });
  }

  return (
    <Card>
      <CardHeader title={t('gest:management.checkin.title', { year })} action={<button type="button" onClick={() => setOpen(true)} className={`${GS_COMPONENTS.btnSecondary} inline-flex items-center gap-1.5`}><Plus size={16} aria-hidden="true" />{t('gest:management.checkin.new')}</button>} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((q) => {
          const c = byQuarter.get(q);
          const avg = c ? checkinAverage(c.answers) : null;
          return (
            <div key={q} className={`flex flex-col gap-1.5 rounded-lg border p-3 ${q === currentQuarter ? 'border-brand-300 dark:border-brand-500/50' : 'border-slate-200 dark:border-slate-700'}`}>
              <div className="flex items-center gap-2"><span className="font-medium text-slate-700 dark:text-slate-200">{t(`gest:management.objectives.q${q}`)}</span>{q === currentQuarter ? <Badge tone="neutral">{t('gest:management.checkin.inProgress')}</Badge> : null}</div>
              {avg !== null ? <><span className="text-head font-semibold tabular-nums text-slate-800 dark:text-slate-100">{avg}<span className="text-field font-normal text-slate-500 dark:text-slate-400"> / {CHECKIN_MAX}</span></span><Badge tone={tone(avg)}>{t('gest:management.checkin.completed')}</Badge>{c?.note ? <p className="line-clamp-2 text-chip text-slate-500 dark:text-slate-400">{c.note}</p> : null}</> : <span className="text-field text-slate-500 dark:text-slate-400">{t('gest:management.checkin.todo')}</span>}
            </div>
          );
        })}
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title={t('gest:management.checkin.dialogTitle', { year })} size="lg">
        <div className="flex flex-col gap-1.5"><label htmlFor="ck-quarter" className={label}>{t('gest:management.checkin.quarter')}</label>
          <select id="ck-quarter" value={quarter} onChange={(e) => setQuarter(Number(e.target.value))} className={`${GS_COMPONENTS.input} sm:max-w-48`}>{[1, 2, 3, 4].map((q) => <option key={q} value={q}>{t(`gest:management.checkin.q${q}`)}</option>)}</select>
        </div>
        <div className="flex flex-col gap-2">
          {CHECKIN_QUESTIONS.map((q) => (
            <div key={q.key} className="flex items-center justify-between gap-3"><label htmlFor={`ck-${q.key}`} className="text-field text-slate-700 dark:text-slate-200">{q.label}</label>
              <select id={`ck-${q.key}`} value={ratings[q.key]} onChange={(e) => setRatings((s) => ({ ...s, [q.key]: e.target.value }))} className={`${GS_COMPONENTS.input} w-20`}>{Array.from({ length: CHECKIN_MAX }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}</select>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-1.5"><label htmlFor="ck-note" className={label}>{t('gest:management.checkin.note')}</label><textarea id="ck-note" rows={4} value={note} onChange={(e) => setNote(e.target.value)} className={GS_COMPONENTS.input} /></div>
        <ModalFooter onCancel={() => setOpen(false)} cancelLabel={t('gest:management.common.cancel')} submitLabel={t('gest:management.checkin.submit')} onSubmit={send} pending={submit.isPending} />
      </Modal>
    </Card>
  );
}

// ─── F-766 Pouls individuel (M-774 : manager exclu cote serveur) ─────────────

export function MemberPulseHistory({ pulses }: { pulses: MemberPulse[] }) {
  const { t } = useTranslation();
  if (pulses.length === 0) return <Card><CardHeader title={t('gest:management.pulse.title')} /><EmptyRow title={t('gest:management.pulse.empty')} description={t('gest:management.pulse.emptyHint')} /></Card>;
  const chrono = [...pulses].sort((a, b) => a.period.localeCompare(b.period));
  const workload = chrono.map((p) => p.workloadScore);
  const energy = chrono.map((p) => p.energyScore);
  const tone = (s: number): BadgeTone => (s >= 4 ? 'success' : s >= 3 ? 'info' : 'warning');
  return (
    <Card>
      <CardHeader title={t('gest:management.pulse.title')} />
      <div className="mb-3 grid gap-3 sm:grid-cols-2">
        <StatCard label={t('gest:management.pulse.workload')} value={`${workload.at(-1)}/${MEMBER_PULSE_MAX}`} hint={<MiniBars data={workload} max={MEMBER_PULSE_MAX} className="text-brand-500" />} />
        <StatCard label={t('gest:management.pulse.energy')} value={`${energy.at(-1)}/${MEMBER_PULSE_MAX}`} hint={<MiniBars data={energy} max={MEMBER_PULSE_MAX} className="text-blue-500" />} />
      </div>
      <ul className="flex flex-col">
        {pulses.map((p) => (
          <li key={p.period} className="flex flex-col gap-1 border-b border-slate-200 py-2.5 last:border-b-0 dark:border-slate-700">
            <span className="text-field font-medium text-slate-700 dark:text-slate-200">{p.period}</span>
            <span className="flex gap-2"><Badge tone={tone(p.workloadScore)}>{t('gest:management.pulse.workloadBadge', { score: p.workloadScore })}</Badge><Badge tone={tone(p.energyScore)}>{t('gest:management.pulse.energyBadge', { score: p.energyScore })}</Badge></span>
            {p.comment ? <p className="text-chip text-slate-500 dark:text-slate-400">{p.comment}</p> : null}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-chip text-slate-500 dark:text-slate-400">{t('gest:management.pulse.footer', { max: MEMBER_PULSE_MAX })}</p>
    </Card>
  );
}

// ─── F-767 Augmentations ──────────────────────────────────────────────────────

export function RaiseHistoryCard({ rows }: { rows: RaiseRow[] }) {
  const { t } = useTranslation();
  const money = (n: number) => CAD0.format(n);
  return (
    <Card>
      <CardHeader title={t('gest:management.raise.title')} />
      {rows.length === 0 ? <p className="text-field text-slate-500 dark:text-slate-400">{t('gest:management.raise.empty')}</p> : (
        <ul className="flex flex-col">
          {rows.map((r) => (
            <li key={r.year} className="flex items-center justify-between gap-3 border-b border-slate-200 py-2 text-field last:border-b-0 dark:border-slate-700">
              <span className="flex items-center gap-2 tabular-nums text-slate-700 dark:text-slate-200">{r.year}{r.echelonKnown ? <span className="text-chip text-slate-500 dark:text-slate-400">{t('gest:management.raise.echelon', { echelon: r.echelon })}</span> : null}<Badge tone={r.cause === 'echelon' ? 'success' : 'neutral'}>{t(`gest:management.raise.cause.${r.cause}`)}</Badge></span>
              <span className="tabular-nums text-slate-600 dark:text-slate-300">{money(r.annual)} <span className={r.deltaPct >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>({r.deltaPct >= 0 ? '+' : ''}{(r.deltaPct * 100).toFixed(1)} %)</span></span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ─── F-768 Forces Gallup + Nova (+ edition) ──────────────────────────────────

export function StrengthsCard({ email, strengths, onError }: { email: string; strengths: EmployeeStrengths | null; onError: OnError }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const gallup = strengths?.gallupStrengths ?? [];
  const nova = strengths?.nova ?? { red: null, yellow: null, green: null, blue: null };
  const hasNova = NOVA_COLORS.some((c) => nova[c] !== null);
  const hasAny = gallup.length > 0 || hasNova;
  return (
    <Card>
      <CardHeader title={t('gest:management.strengths.title')} action={<button type="button" onClick={() => setOpen(true)} className={GS_COMPONENTS.btnSecondary}>{t('gest:management.common.edit')}</button>} />
      {!hasAny ? <EmptyRow title={t('gest:management.strengths.empty')} description={t('gest:management.strengths.emptyHint')} /> : (
        <div className="flex flex-col gap-3">
          {gallup.length > 0 ? <div className="flex flex-wrap gap-1.5">{gallup.map((s, i) => <Badge key={`${s}-${i}`} tone="brand"><span className="mr-1 text-slate-400 tabular-nums">{i + 1}.</span>{s}</Badge>)}</div> : <p className="text-field text-slate-500 dark:text-slate-400">{t('gest:management.strengths.noGallup')}</p>}
          {hasNova ? (
            <ul className="flex flex-col gap-2">
              {NOVA_COLORS.map((c) => {
                const v = nova[c];
                const pct = v === null ? 0 : Math.max(0, Math.min(100, v));
                return <li key={c} className="flex items-center gap-2"><span className="w-12 shrink-0 text-chip text-slate-600 dark:text-slate-300">{NOVA_LABELS[c]}</span><span className="relative h-2 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700"><span className={`${NOVA_BAR_CLASSES[c]} block h-full`} style={{ width: `${pct}%` }} /></span><span className="w-10 shrink-0 text-right text-chip tabular-nums text-slate-500 dark:text-slate-400">{v === null ? '—' : `${v} %`}</span></li>;
              })}
            </ul>
          ) : <p className="text-field text-slate-500 dark:text-slate-400">{t('gest:management.strengths.noNova')}</p>}
        </div>
      )}
      <EditStrengthsDialog email={email} open={open} onClose={() => setOpen(false)} strengths={strengths} onError={onError} />
    </Card>
  );
}

const DOMAINS: GallupDomain[] = ['strategic', 'influencing', 'relationship', 'executing'];

function EditStrengthsDialog({ email, open, onClose, strengths, onError }: { email: string; open: boolean; onClose: () => void; strengths: EmployeeStrengths | null; onError: OnError }) {
  const { t } = useTranslation();
  const save = useSetStrengths();
  const [gallup, setGallup] = useState<string[]>(() => [0, 1, 2, 3, 4].map((i) => strengths?.gallupStrengths[i] ?? ''));
  const nova0 = strengths?.nova ?? { red: null, yellow: null, green: null, blue: null };
  const [nova, setNova] = useState<Record<string, string>>(() => Object.fromEntries(NOVA_COLORS.map((c) => [c, nova0[c] !== null ? String(nova0[c]) : ''])));
  const [err, setErr] = useState<string | null>(null);

  function submit() {
    const picked = gallup.map((s) => s.trim()).filter(Boolean);
    if (new Set(picked).size !== picked.length) { setErr(t('gest:management.errors.duplicate_strength')); return; }
    if (picked.some((s) => !isValidStrength(s))) { setErr(t('gest:management.errors.invalid_strengths')); return; }
    setErr(null);
    save.mutate({ email, payload: { gallupStrengths: picked, nova: { red: nova.red === '' ? null : Number(nova.red), yellow: nova.yellow === '' ? null : Number(nova.yellow), green: nova.green === '' ? null : Number(nova.green), blue: nova.blue === '' ? null : Number(nova.blue) } } }, {
      onSuccess: () => onClose(),
      onError: (x) => onError(managementErrorCode(x)),
    });
  }

  return (
    <Modal open={open} onClose={onClose} title={t('gest:management.strengths.dialogTitle')} size="lg">
      <div className="flex flex-col gap-2">
        <span className={label}>{t('gest:management.strengths.gallupLabel')}</span>
        <div className="grid gap-3 sm:grid-cols-2">
          {gallup.map((val, n) => (
            <select key={n} aria-label={t('gest:management.strengths.strengthN', { n: n + 1 })} value={val} onChange={(e) => setGallup((g) => g.map((x, i) => (i === n ? e.target.value : x)))} className={GS_COMPONENTS.input}>
              <option value="">{t('gest:management.strengths.undefinedOption')}</option>
              {DOMAINS.map((d) => <optgroup key={d} label={GALLUP_DOMAIN_LABELS[d]}>{GALLUP_STRENGTHS_BY_DOMAIN[d].map((s) => <option key={s} value={s}>{s}</option>)}</optgroup>)}
            </select>
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <span className={label}>{t('gest:management.strengths.novaLabel')}</span>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {NOVA_COLORS.map((c) => (
            <div key={c} className="flex flex-col gap-1.5"><label htmlFor={`nova-${c}`} className="text-chip text-slate-600 dark:text-slate-300">{NOVA_LABELS[c]}</label><input id={`nova-${c}`} type="number" min={0} max={100} value={nova[c]} onChange={(e) => setNova((s) => ({ ...s, [c]: e.target.value }))} className={GS_COMPONENTS.input} /><span className="text-chip text-slate-500 dark:text-slate-400">{NOVA_DESCRIPTIONS[c]}</span></div>
          ))}
        </div>
      </div>
      {err ? <p role="alert" className="text-field text-red-600 dark:text-red-400">{err}</p> : null}
      <ModalFooter onCancel={onClose} cancelLabel={t('gest:management.common.cancel')} submitLabel={t('gest:management.common.save')} onSubmit={submit} pending={save.isPending} />
    </Modal>
  );
}

// ─── F-769/770/779 Objectifs ──────────────────────────────────────────────────

const OBJ_STATUS = ['pending', 'in_progress', 'done'] as const;
const OBJ_QUARTERS = ['q1', 'q2', 'q3', 'q4'] as const;

export function ObjectivesCard({ email, year, objectives, onError }: { email: string; year: number; objectives: EmployeeObjective[]; onError: OnError }) {
  const { t } = useTranslation();
  const create = useCreateObjective();
  const setStatus = useUpdateObjectiveStatus();
  const del = useDeleteObjective();
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState<'annual' | 'q1' | 'q2' | 'q3' | 'q4'>('annual');
  const [title, setTitle] = useState('');
  const [status, setStatusForm] = useState<'pending' | 'in_progress' | 'done'>('pending');
  const [targetDate, setTargetDate] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const total = objectives.length;
  const done = objectives.filter((o) => o.status === 'done').length;
  const groups: { key: string; label: string }[] = [
    { key: 'annual', label: t('gest:management.objectives.annual') },
    { key: 'q1', label: 'T1' }, { key: 'q2', label: 'T2' }, { key: 'q3', label: 'T3' }, { key: 'q4', label: 'T4' },
  ];

  function submit() {
    if (!title.trim()) { setErr(t('gest:management.errors.invalid')); return; }
    setErr(null);
    create.mutate({ email, payload: { period, year, title: title.trim(), status, targetDate: targetDate || null } }, {
      onSuccess: () => { setOpen(false); setTitle(''); setTargetDate(''); },
      onError: (x) => onError(managementErrorCode(x)),
    });
  }

  return (
    <Card>
      <CardHeader title={t('gest:management.objectives.title', { year })} action={<button type="button" onClick={() => setOpen(true)} className={`${GS_COMPONENTS.btnSecondary} inline-flex items-center gap-1.5`}><Plus size={16} aria-hidden="true" />{t('gest:management.objectives.new')}</button>} />
      {total === 0 ? <EmptyRow title={t('gest:management.objectives.empty')} /> : (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2"><span className="text-head font-semibold tabular-nums text-slate-800 dark:text-slate-100">{done}/{total}</span><span className="text-field text-slate-500 dark:text-slate-400">{t('gest:management.objectives.doneCount', { pct: total > 0 ? Math.round((done / total) * 100) : 0 })}</span></div>
          <GaugeBar value={done} max={total} />
          <div className="flex flex-col gap-4">
            {groups.map((g) => {
              const items = objectives.filter((o) => o.period === g.key);
              if (items.length === 0) return null;
              return (
                <div key={g.key} className="flex flex-col gap-2">
                  <p className="text-chip font-medium uppercase tracking-wide text-slate-400">{g.label}</p>
                  {items.map((o) => (
                    <DataRow key={o.id} title={o.title} subtitle={o.targetDate ? t('gest:management.objectives.due', { date: o.targetDate }) : undefined} trailing={<>
                      <select value={o.status} onChange={(e) => setStatus.mutate({ email, payload: { objectiveId: o.id, status: e.target.value } }, { onError: (x) => onError(managementErrorCode(x)) })} aria-label={t('gest:management.objectives.statusLabel')} className={`${GS_COMPONENTS.input} h-8 py-0`}>{OBJ_STATUS.map((s) => <option key={s} value={s}>{t(`gest:management.status.${s}`)}</option>)}</select>
                      <button type="button" onClick={() => del.mutate({ email, payload: { objectiveId: o.id } }, { onError: (x) => onError(managementErrorCode(x)) })} className={`${GS_COMPONENTS.btnGhost} text-red-600 dark:text-red-400`}>{t('gest:management.common.delete')}</button>
                    </>} />
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title={t('gest:management.objectives.dialogTitle', { year })}>
        <div className="flex flex-col gap-1.5"><label htmlFor="obj-period" className={label}>{t('gest:management.objectives.period')}</label>
          <select id="obj-period" value={period} onChange={(e) => setPeriod(e.target.value as typeof period)} className={GS_COMPONENTS.input}>
            <option value="annual">{t('gest:management.objectives.annual')}</option>{OBJ_QUARTERS.map((q) => <option key={q} value={q}>{t(`gest:management.objectives.${q}`)}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1.5"><label htmlFor="obj-title" className={label}>{t('gest:management.objectives.objectiveTitle')}</label><input id="obj-title" maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} className={GS_COMPONENTS.input} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5"><label htmlFor="obj-status" className={label}>{t('gest:management.objectives.statusLabel')}</label><select id="obj-status" value={status} onChange={(e) => setStatusForm(e.target.value as typeof status)} className={GS_COMPONENTS.input}>{OBJ_STATUS.map((s) => <option key={s} value={s}>{t(`gest:management.status.${s}`)}</option>)}</select></div>
          <div className="flex flex-col gap-1.5"><label htmlFor="obj-date" className={label}>{t('gest:management.objectives.due', { date: '' })}</label><input id="obj-date" type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} className={GS_COMPONENTS.input} /></div>
        </div>
        {err ? <p role="alert" className="text-field text-red-600 dark:text-red-400">{err}</p> : null}
        <ModalFooter onCancel={() => setOpen(false)} cancelLabel={t('gest:management.common.cancel')} submitLabel={t('gest:management.objectives.submit')} onSubmit={submit} pending={create.isPending} />
      </Modal>
    </Card>
  );
}
